import { DateTime } from 'luxon';
export type ClockGranularity = keyof typeof IncrementAmounts;

type ClockProps = {
  from: string;
  to: string;
  granularity: ClockGranularity;
};

enum IncrementAmounts {
  minute = 60000,
  hour = 3600000,
  day = 86400000,
  week = 4233600000
}
export class Clock {
  public readonly granularity: ClockGranularity;

  private _to: DateTime;
  private _from: DateTime;
  private _duration: number;
  private _currentDateTime: DateTime;
  private _currentPosition: number = 0;

  public constructor(config: ClockProps) {
    this._to = DateTime.fromISO(config.to);
    this._from = DateTime.fromISO(config.from);
    this._currentDateTime = DateTime.fromISO(config.from);
    this._duration = this._to.diff(this._from).milliseconds;
    this.granularity = config.granularity || 'hour';
  }

  private setNow(target: DateTime): DateTime {
    this._currentDateTime =
      target > this._to ? this._to : target < this._from ? this._from : target;
    this.setPosition();
    return this._currentDateTime;
  }

  private setPosition() {
    this._currentPosition = Number(
      ((this._currentDateTime.toMillis() - this._from.toMillis()) / this._duration).toPrecision(4)
    );
  }

  private get incrementMilli(): number {
    return IncrementAmounts[this.granularity];
  }

  public get now(): DateTime {
    return this._currentDateTime;
  }

  public get position(): number {
    return this._currentPosition;
  }

  public get to(): DateTime {
    return this._to;
  }

  public get from(): DateTime {
    return this._from;
  }

  public increment(steps: number = 1): DateTime {
    const target: DateTime = this._currentDateTime.plus(this.incrementMilli * steps);
    return this.setNow(target);
  }

  public decrement(steps: number = 1): DateTime {
    const target: DateTime = this._currentDateTime.plus(this.incrementMilli * steps);
    return this.setNow(target);
  }

  public toStart(): DateTime {
    return this.setNow(this._from);
  }

  public toEnd(): DateTime {
    return this.setNow(this._to);
  }

  public get isAtEnd(): boolean {
    return this._currentDateTime.toMillis() === this._to.toMillis();
  }

  public get isAtStart(): boolean {
    return this._currentDateTime.toMillis() === this._from.toMillis();
  }
}
